import React, { useState, useEffect, useRef } from 'react';
import { InboxMessage, ChatConversation } from '../domain/types';
import { MessageCard } from './MessageCard';
import {
  Loader2,
  MessageCircle,
  Send,
  Lock,
  Paperclip,
  Mic,
  Square,
  X,
  FileText,
  ArrowLeft,
} from 'lucide-react';
import { INBOX_ACCEPT, validateOutboundFile } from '../domain/mediaLimits';

interface PendingAttachment {
  id: string;
  file: File;
  previewUrl?: string;
  mediaType: string;
  isVoice?: boolean;
}

interface Props {
  messages: InboxMessage[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  selectedConversation?: ChatConversation;
  onSendMessage: (message: string) => void;
  onSendMediaFiles?: (
    files: File[],
    caption?: string,
    voiceFlags?: boolean[]
  ) => Promise<void> | void;
  sendingMedia?: boolean;
  onBack?: () => void;
}

export const ChatWindow: React.FC<Props> = ({
  messages,
  loading,
  hasMore,
  onLoadMore,
  selectedConversation,
  onSendMessage,
  onSendMediaFiles,
  sendingMedia = false,
  onBack,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [attachError, setAttachError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const recordTimerRef = useRef<number | null>(null);
  const recordStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  // Clear drafts when switching conversation
  useEffect(() => {
    setPending((prev) => {
      prev.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      return [];
    });
    setInputValue('');
    setAttachError(null);
    stopRecordingInternal(false);
  }, [selectedConversation?.phone_number]);

  useEffect(() => {
    return () => {
      pending.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      stopRecordingInternal(false);
    };
  }, []);

  if (!selectedConversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-[#f8f9fa] text-gray-400">
        <MessageCircle className="w-16 h-16 mb-4 text-gray-200" />
        <p className="text-lg">Selecciona una conversación para comenzar</p>
      </div>
    );
  }

  const canReply = selectedConversation?.can_reply;
  const composerBusy = sendingMedia || isRecording;
  const hasPending = pending.length > 0;
  const canSend =
    canReply &&
    !composerBusy &&
    (hasPending || !!inputValue.trim()) &&
    (!hasPending || !!onSendMediaFiles);

  function stopRecordingInternal(save: boolean) {
    if (recordTimerRef.current) {
      window.clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        if (!save) {
          recorder.ondataavailable = null;
          recorder.onstop = null;
        }
        recorder.stop();
      } catch {
        /* ignore */
      }
    }
    mediaRecorderRef.current = null;
    recordStreamRef.current?.getTracks().forEach((t) => t.stop());
    recordStreamRef.current = null;
    setIsRecording(false);
    setRecordSeconds(0);
  }

  const stageFiles = (files: File[], asVoice = false) => {
    const next: PendingAttachment[] = [];
    const errors: string[] = [];

    for (const file of files) {
      const validation = validateOutboundFile(file);
      if (validation.ok === false) {
        errors.push(validation.error);
        continue;
      }
      const mediaType = validation.mediaType;
      const previewUrl =
        mediaType === 'image' || mediaType === 'video' || mediaType === 'audio'
          ? URL.createObjectURL(file)
          : undefined;
      next.push({
        id: `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 7)}`,
        file,
        previewUrl,
        mediaType,
        isVoice: asVoice && mediaType === 'audio',
      });
    }

    if (errors.length) {
      setAttachError(errors[0]);
    } else {
      setAttachError(null);
    }
    if (next.length) {
      setPending((prev) => [...prev, ...next]);
    }
  };

  const removePending = (id: string) => {
    setPending((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const handleSend = async () => {
    if (!canSend) return;

    if (hasPending && onSendMediaFiles) {
      const files = pending.map((p) => p.file);
      const voiceFlags = pending.map((p) => !!p.isVoice);
      const caption = inputValue.trim() || undefined;
      try {
        await onSendMediaFiles(files, caption, voiceFlags);
        pending.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
        setPending([]);
        setInputValue('');
        setAttachError(null);
      } catch (err: any) {
        setAttachError(err?.message || 'Error al enviar archivos');
      }
      return;
    }

    if (inputValue.trim()) {
      onSendMessage(inputValue);
      setInputValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && canSend) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handleAttachClick = () => {
    setAttachError(null);
    fileInputRef.current?.click();
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list?.length || !canReply) {
      e.target.value = '';
      return;
    }
    stageFiles(Array.from(list));
    e.target.value = '';
  };

  const startRecording = async () => {
    if (!canReply || composerBusy || !onSendMediaFiles) return;
    setAttachError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      recordChunksRef.current = [];

      const preferredTypes = [
        'audio/ogg;codecs=opus',
        'audio/webm;codecs=opus',
        'audio/webm',
      ];
      const mimeType =
        preferredTypes.find((t) => MediaRecorder.isTypeSupported(t)) || '';

      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) recordChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blobType = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(recordChunksRef.current, { type: blobType });
        recordChunksRef.current = [];
        recordStreamRef.current?.getTracks().forEach((t) => t.stop());
        recordStreamRef.current = null;

        if (blob.size < 1) {
          setAttachError('No se pudo grabar el audio. Inténtalo de nuevo.');
          return;
        }

        const ext = blobType.includes('ogg') ? 'ogg' : 'webm';
        const file = new File([blob], `nota-voz-${Date.now()}.${ext}`, {
          type: blobType.split(';')[0],
        });
        stageFiles([file], true);
      };

      recorder.start();
      setIsRecording(true);
      setRecordSeconds(0);
      recordTimerRef.current = window.setInterval(() => {
        setRecordSeconds((s) => s + 1);
      }, 1000);
    } catch {
      setAttachError('No se pudo acceder al micrófono. Revisa los permisos del navegador.');
      stopRecordingInternal(false);
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;
    if (recordTimerRef.current) {
      window.clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
    setIsRecording(false);
    setRecordSeconds(0);
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    return phone.split('|')[0];
  };

  const formatRecordTime = (total: number) => {
    const m = Math.floor(total / 60)
      .toString()
      .padStart(2, '0');
    const s = (total % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className="flex flex-col h-full bg-[#f8f9fa] relative">
      <div className="h-[72px] bg-white px-3 md:px-6 flex items-center justify-between shadow-sm border-b border-gray-200 sticky top-0 z-10 w-full shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="md:hidden p-2 -ml-1 rounded-full text-gray-600 hover:bg-gray-100 shrink-0"
              title="Volver a conversaciones"
              aria-label="Volver a conversaciones"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-800 truncate">
              {selectedConversation.contact_name || formatPhoneNumber(selectedConversation.phone_number)}
            </h2>
            <div className="text-xs text-gray-500 truncate">
              {formatPhoneNumber(selectedConversation.phone_number)}
            </div>
          </div>
        </div>
      </div>

      {hasMore && !loading && (
        <div className="flex justify-center py-3 bg-[#f8f9fa] shrink-0 border-b border-gray-100">
          <button
            onClick={onLoadMore}
            className="text-sm text-primary hover:underline font-medium"
          >
            Cargar mensajes anteriores
          </button>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      )}

      <div ref={containerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <p className="text-sm">No hay mensajes en esta conversación</p>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg._id}
            className={`flex ${msg.direction === 'sent' ? 'justify-end' : 'justify-start'}`}
          >
            <div className="w-full max-w-[85%]">
              <MessageCard message={msg} />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-transparent px-4 py-3 shrink-0">
        {!canReply && (
          <div className="mb-3 px-4 py-3 bg-red-50 border border-red-100 rounded-lg flex items-center gap-3">
            <Lock className="w-5 h-5 text-red-500" />
            <p className="text-sm text-red-800">
              Han pasado más de 24 horas desde el último mensaje del usuario. Según las políticas de Meta, no puedes enviar mensajes de texto libre hasta que el usuario vuelva a escribir.
            </p>
          </div>
        )}

        {attachError && (
          <div className="mb-2 text-xs text-red-600">{attachError}</div>
        )}

        {sendingMedia && (
          <div className="mb-2 flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Enviando archivos…
          </div>
        )}

        {isRecording && (
          <div className="mb-2 flex items-center gap-2 text-xs text-red-600">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            Grabando {formatRecordTime(recordSeconds)} — pulsa detener para adjuntar la nota
          </div>
        )}

        {hasPending && (
          <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
            {pending.map((item) => (
              <div
                key={item.id}
                className="relative shrink-0 w-24 h-24 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden"
              >
                {item.mediaType === 'image' && item.previewUrl && (
                  <img src={item.previewUrl} alt="" className="w-full h-full object-cover" />
                )}
                {item.mediaType === 'video' && item.previewUrl && (
                  <video src={item.previewUrl} className="w-full h-full object-cover" muted />
                )}
                {item.mediaType === 'audio' && (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-1 px-1">
                    <Mic className="w-5 h-5 text-gray-500" />
                    <span className="text-[10px] text-gray-500 text-center truncate w-full">
                      {item.isVoice ? 'Nota de voz' : 'Audio'}
                    </span>
                  </div>
                )}
                {item.mediaType === 'document' && (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-1 px-1">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <span className="text-[10px] text-gray-500 text-center truncate w-full px-1">
                      {item.file.name}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removePending(item.id)}
                  disabled={composerBusy}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 hover:bg-black/80 disabled:opacity-50"
                  title="Quitar"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 items-center">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={INBOX_ACCEPT}
            multiple
            onChange={handleFilesSelected}
            disabled={!canReply || composerBusy || !onSendMediaFiles}
          />
          <button
            type="button"
            onClick={handleAttachClick}
            disabled={!canReply || composerBusy || !onSendMediaFiles}
            className="w-8 h-8 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center shrink-0"
            title="Adjuntar archivos"
          >
            <Paperclip className="w-4 h-4" />
          </button>

          {!isRecording ? (
            <button
              type="button"
              onClick={() => void startRecording()}
              disabled={!canReply || sendingMedia || !onSendMediaFiles}
              className="w-8 h-8 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center shrink-0"
              title="Grabar nota de voz"
            >
              <Mic className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={stopRecording}
              className="w-8 h-8 rounded-full border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors flex items-center justify-center shrink-0"
              title="Detener grabación"
            >
              <Square className="w-4 h-4" />
            </button>
          )}

          <input
            type="text"
            placeholder={
              !canReply
                ? 'Ventana de servicio al cliente cerrada (24h)'
                : hasPending
                  ? 'Añade un pie de foto (opcional) y envía…'
                  : 'Escribe un mensaje...'
            }
            className="flex-1 min-w-0 h-12 px-5 border border-gray-200 bg-white rounded-full text-sm text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 transition-all placeholder:text-gray-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={!canReply || composerBusy}
          />
          <button
            onClick={() => void handleSend()}
            disabled={!canSend}
            className="w-8 h-8 rounded-full bg-teal-600 hover:bg-teal-500 disabled:opacity-30 disabled:cursor-not-allowed text-white transition-colors flex items-center justify-center shrink-0"
            title="Enviar"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
